import { Pipe, PipeTransform } from '@angular/core';
import { ItemConfig, ItemProfile, ItemResource, Link, ResourceMeta } from '@ngx-hal/models';
import { PropertiesProfilesPipe } from '../properties-profiles/properties-profiles.pipe';
import { PropertyResourceMetaPipe } from '../property-resource-meta/property-resource-meta.pipe';

@Pipe({
  name: 'sortParams',
  standalone: false
})
export class SortParamsPipe implements PipeTransform {
  /**
   * Transforms a map of item profiles and resource metadata into a sort parameter object
   * suitable for use in query parameters. It recursively traverses the item profiles and
   * their properties, constructing a list of sort strings based on the provided item configuration.
   *
   * @template Entity - The type of the item resource.
   * @param itemsProfileMap - A map associating resource profile hrefs with their corresponding item profiles.
   * @param resourceMeta - Metadata describing the resource, including its profile.
   * @param itemConfig - (Optional) Configuration object specifying sort directions for properties.
   * @param prefix - (Optional) String prefix to prepend to property names in the sort strings (used for nested properties).
   * @returns An object containing a `sort` property, which is an array of sort strings, or an empty object if no sort parameters are generated.
   */
  transform<Entity extends ItemResource>(
    itemsProfileMap: Map<Link['href'], ItemProfile<Entity>>,
    resourceMeta: ResourceMeta<Entity>,
    itemConfig?: ItemConfig<Entity>,
    prefix?: string,
  ): { sort?: string[] } {
    const itemProfile = itemsProfileMap.get(resourceMeta.profile.href);

    return itemProfile
      ? Object.fromEntries(
          [
            [
              'sort',
              new PropertiesProfilesPipe()
                .transform(itemProfile, itemConfig, { remove: { children: true } })
                .map(property => {
                  const subResourceMeta = new PropertyResourceMetaPipe().transform(property);

                  if (subResourceMeta) {
                    return (
                      this.transform(
                        itemsProfileMap,
                        subResourceMeta,
                        itemConfig?.[property.alps.name],
                        `${property.alps.name}.`,
                      )?.sort ?? []
                    );
                  } else if (itemConfig?.[property.alps.name]?._direction) {
                    if(itemConfig?.[property.alps.name]?._direction === 'none') {
                      return [];
                    }
                    return [`${prefix ?? ''}${property.alps.name},${itemConfig?.[property.alps.name]?._direction}`];
                  }

                  return [];
                })
                .flat(),
            ],
          ].filter(([, value]) => value.length > 0),
        )
      : {};
  }
}
